<template>
  <BCard>
    <BRow class="items-center">
      <BCol lg="6">
        <strong class="text-bold text-black text-2xl">Monitoring Pickup</strong>
      </BCol>
      <BCol
        lg="6"
        class="flex justify-end items-center"
      >
        <b-input-group>
          <template #prepend>
            <b-dropdown
              v-model="searchType"
              variant="outline-muted"
              toggle-class="border bg-transparent custom-caret-toggle"
            >
              <template #button-content>
                <span>{{ searchType }}</span>
                <svg
                  class="custom-caret-svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M7 10l5 5 5-5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </template>
              <b-dropdown-item @click.stop="searchType = 'Email'">
                Email
              </b-dropdown-item>
              <b-dropdown-item @click.stop="searchType = 'Resi'">
                Resi
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <BFormInput
            v-model="search"
            :placeholder="`Ketik ${searchType} disini...`"
            style="width: 45%; margin-right: 10px"
            @input="searchData"
          />
        </b-input-group>
        <div class="position-relative d-inline-block">
          <b-dropdown
            ref="filterDropdown"
            size="sm"
            variant="primary"
            right
            no-caret
            @hide="preventDropdownClose"
          >
            <template #button-content>
              <img
                class="w-7"
                src="https://storage.googleapis.com/komerce/assets/komerce-icon/Putih/filter-search.svg"
                alt="filter-icon"
              >
            </template>
            <b-dropdown-text class="text-black font-semibold">
              Filter
            </b-dropdown-text>
            <b-dropdown-divider />
            <b-dropdown-item @click.stop>
              <b-dropdown
                v-model="expedition"
                :text="expedition === '' ? 'Ekspedisi' : expedition"
                variant="plain"
                toggle-class="bg-transparent hover-none"
                dropleft
                no-caret
              >
                <b-dropdown-item @click.stop="selectExpedition('')">
                  Semua
                </b-dropdown-item>
                <b-dropdown-item
                  v-for="(shipping, idx) in expeditionList"
                  :key="idx"
                  @click.stop="selectExpedition(shipping.shipping_name)"
                >
                  {{ shipping.shipping_name }}
                </b-dropdown-item>
              </b-dropdown>
            </b-dropdown-item>
            <b-dropdown-item @click.stop>
              <b-dropdown
                v-model="firstPickup"
                :text="firstPickup === '' ? 'Pickup' : 'First Pickup'"
                variant="plain"
                toggle-class="bg-transparent hover-none"
                dropleft
                no-caret
              >
                <b-dropdown-item @click.stop="selectFirstPickup('')">
                  Semua
                </b-dropdown-item>
                <b-dropdown-item @click.stop="selectFirstPickup(true)">
                  First Pickup
                </b-dropdown-item>
              </b-dropdown>
            </b-dropdown-item>
          </b-dropdown>
          <span
            v-if="expedition !== '' || firstPickup"
            class="position-absolute -top-4 -end-1 translate-middle badge rounded-pill bg-white text-primary border-primary"
            style="font-size: 0.7rem"
          >
            {{ (expedition !== '' ? 1 : 0) + (firstPickup ? 1 : 0) }}
          </span>
        </div>
      </BCol>
    </BRow>
    <BOverlay
      :show="loading"
      spinner-variant="primary"
      variant="light"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <BTable
        id="table"
        :items="items"
        :fields="fields"
        show-empty
        empty-text="Tidak ada data yang ditampilkan."
        responsive
        class="mt-1"
      >
        <template #cell(full_name)="data">
          <div class="d-flex">
            <div
              class="d-flex align-items-center"
              :class="data.item.user_level ? 'w-10 mr-1' : ''"
            >
              <img
                v-if="data.item.user_level"
                :src="getBadgeLevel(data.item.user_level)"
                class="w-full"
                alt="badge"
              >
            </div>
            <div>
              <div v-b-tooltip.hover.top="data.value.length > 20 ? data.value : ''">
                {{ data.value.length > 20 ? data.value.substring(0, 20) + '...' : data.value }}
              </div>
              <div class="text-sm">
                {{ data.item.email }}
              </div>
            </div>
          </div>
        </template>
        <template #cell(shipping_image)="data">
          <img
            :src="data.value"
            alt="shipping-logo"
            style="width: 50px; margin: auto"
          >
        </template>
        <template #cell(order_total)="data">
          <div class="min-w-[150px] d-flex">
            <div class="align-self-center">
              {{ data.value }}
            </div>
            <img
              v-if="data.item.is_first_pickup"
              v-b-tooltip.hover.top="'New First Pickup'"
              class="ml-1"
              src="https://storage.googleapis.com/komerce/Komship/firstPickupIcon.svg"
              alt="first-pickup"
            >
          </div>
        </template>
        <template #cell(cnote)="data">
          <span
            v-b-tooltip.hover.top="'Copy Resi'"
            class="cursor-pointer"
            @click="copyResi(data.value)"
          >
            {{ data.value }}
          </span>
        </template>
        <template #cell(city)="data">
          <span
            v-b-tooltip.hover.top="'Copy Detail Pickup'"
            class="cursor-pointer"
            @click="copyDetailData(data.item)"
          >{{ data.value }}</span>
        </template>
        <template #cell(pickup_date)="data">
          <div
            class="min-w-[150px]"
            style="font-size: 13px"
          >
            {{ DAY_MONTH_YEAR(data.value) }}
          </div>
          <div class="text-sm">
            {{ TIME(data.item.pickup_time) }} WIB
          </div>
        </template>
        <template #cell(delay)="data">
          <div class="min-w-[150px]">
            {{ handleDelayAction(data.item).delayTime }}
          </div>
          <div
            class="text-white rounded-xl"
            :class="handleDelayAction(data.item).className"
          >
            {{ handleDelayAction(data.item).status }}
          </div>
        </template>
      </BTable>
      <BRow v-if="size(items) !== 0">
        <BCol
          cols="12"
          class="d-flex justify-content-between"
        >
          <div
            class="
              bg-light
              d-flex
              justify-content-center
              align-items-center
              p-50
              rounded
            "
          >
            <span class="mr-50"> List per halaman: </span>
            <BButton
              v-for="page in optionsPage"
              :key="page"
              class="btn-icon"
              size="sm"
              :variant="totalPerPage === page ? 'primary' : 'flat-dark'"
              @click="setPerPage(page)"
            >
              {{ page }}
            </BButton>
          </div>

          <BPagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="totalPerPage"
            first-number
            last-number
            class="pagination-primary"
            @change="handlePageChange"
          >
            <template #prev-text>
              <feather-icon
                size="18"
                icon="ChevronLeftIcon"
              />
            </template>
            <template #next-text>
              <feather-icon
                size="18"
                icon="ChevronRightIcon"
              />
            </template>
          </BPagination>
        </BCol>
      </BRow>
    </BOverlay>
  </BCard>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import size from 'lodash/size'
import { DAY_MONTH_YEAR } from '@/libs/filterDate'
import configColumn from './config'

export default {
  data() {
    return {
      loading: true,
      search: '',
      searchType: 'Email',
      items: [],
      fields: configColumn,
      expedition: '',
      firstPickup: '',
      optionsPage: [50, 100, 200],
      totalPerPage: 50,
      currentPage: 1,
      totalRows: 0,
      isClickOutside: false,
      DAY_MONTH_YEAR,
      size,
    }
  },
  computed: {
    ...mapState('filter', ['expeditionList']),
  },
  async mounted() {
    if (this.expeditionList.length === 0) {
      this.$store.dispatch('filter/getUpdateExpedition')
    }
    await this.getData()
    document.addEventListener('click', this.handleDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick)
  },
  methods: {
    async getData() {
      this.loading = true
      const params = {
        per_page: this.totalPerPage,
        page: this.currentPage,
        shipping: this.expedition,
        is_first_pickup: this.firstPickup,
      }

      if (this.search !== '') {
        const paramKey = this.searchType === 'Email' ? 'email' : 'cnote'
        params[paramKey] = this.search
      }
      const url = 'komship/api/v1/admin/monitoring/pickup'
      await this.$http_new
        .get(url, { params })
        .then(res => {
          const { data } = res.data
          this.items = data.data
          this.totalRows = data.total_data
          this.loading = false
        })
        .catch(err => {
          this.$toast_error({ message: err.response.message })
          this.loading = false
        })
    },
    handlePageChange(page) {
      this.currentPage = page
      this.getData()
    },
    searchData: _.debounce(function search() {
      if (this.search.length === 0 || this.search.length >= 3) {
        this.defaultFilter('search')
        this.getData()
      }
    }, 1000),
    copyResi(value) {
      navigator.clipboard.writeText(value)
      this.$toast_success({ title: 'Resi berhasil disalin', timeout: 1000 })
    },
    copyDetailData(value) {
      const username = `Nama Penanggung Jawab: ${value.pic_name}`
      const phoneNumber = `No.Hp: ${value.pic_phone}`
      const resi = `Resi sample: ${value.cnote}`
      const createdAt = `Pengajuan Pickup: ${moment(
        value.pickup_date,
      ).format('DD MMMM YYYY')} Pukul ${value.pickup_time} WIB`
      const pickupcity = `Kota Pickup: ${value.city}`
      const address = `Alamat: ${value.address_detail}`
      const data = `${username}\n${phoneNumber}\n${address}\n${pickupcity}\n${resi}\n${createdAt}`

      navigator.clipboard.writeText(data)
      this.$toast_success({ title: 'Data berhasil disalin', timeout: 1000 })
    },
    handleDelayAction(value) {
      const pickupDateTime = moment(`${value.pickup_date} ${value.pickup_time}`, 'YYYY-MM-DD HH:mm:ss')
      const today = moment()
      const diffDays = today.diff(pickupDateTime, 'days')
      const diffHours = today.diff(pickupDateTime, 'hours') % 24

      const isOverdue = diffDays > 0 || (diffDays === 0 && diffHours > 0)
      const status = isOverdue ? 'Terlewati' : 'Akan Datang'
      const className = isOverdue ? 'bg-[#E31A1A]' : 'bg-[#08A0F7]'

      let delayTime = ''
      if (diffDays !== 0) {
        const days = Math.abs(diffDays)
        const hours = Math.abs(diffHours)
        delayTime = `${days} hari ${hours} jam`
      } else {
        delayTime = `${Math.abs(diffHours)} jam`
      }

      return { delayTime, status, className }
    },
    setPerPage(page) {
      this.currentPage = 1
      this.totalPerPage = page
      this.getData()
    },
    getBadgeLevel(level) {
      if (level === 'Saudagar') return 'https://storage.googleapis.com/komerce/assets/svg/Saudagar.svg'
      if (level === 'Juragan') return 'https://storage.googleapis.com/komerce/assets/icons/badge_juragan.svg'
      return ''
    },
    TIME(value) {
      return moment(value, 'HH:mm:ss').format('HH:mm')
    },
    selectExpedition(value) {
      this.defaultFilter()
      this.expedition = value
      this.getData()
      this.$refs.filterDropdown.hide()
    },
    selectFirstPickup(value) {
      this.defaultFilter()
      this.firstPickup = value
      this.getData()
      this.$refs.filterDropdown.hide()
    },
    defaultFilter(val) {
      this.currentPage = 1
      this.totalPerPage = 50
      if (val === 'search') {
        this.expedition = ''
        this.firstPickup = ''
      } else {
        this.search = ''
      }
    },
    preventDropdownClose(e) {
      if (this.isClickOutside) {
        this.isClickOutside = false
        return
      }

      if (this.loading) {
        this.$refs.filterDropdown.hide()
      } else {
        e.preventDefault()
        this.$refs.filterDropdown.show()
      }
    },
    handleDocumentClick(e) {
      if (this.$refs.filterDropdown && this.$refs.filterDropdown.$el) {
        const dropdown = this.$refs.filterDropdown.$el
        const isClickInsideDropdown = dropdown.contains(e.target)

        if (!isClickInsideDropdown && this.$refs.filterDropdown.visible) {
          this.isClickOutside = true
          this.$refs.filterDropdown.hide()
        }
      }
    },
  },
}
</script>

<style scoped>
::v-deep .form-control:focus {
  border-color: inherit !important;
  box-shadow: none !important;
}
::v-deep .input-group:not(:focus) .form-control {
  border-color: inherit !important;
  box-shadow: none !important;
}
::v-deep .dropdown-toggle::after {
  border-top-color: currentColor !important;
  opacity: 1 !important;
}
::v-deep .custom-caret-toggle::after {
  display: none !important;
}
.custom-caret-svg {
  vertical-align: middle;
  margin-left: 6px;
  color: inherit;
  display: inline-block;
}
</style>
